import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { LTModal } from '@lambdatestincprivate/lt-components';
import styles from './steps.module.css';

/**
 * "Show script" modal for EXECUTE_CUSTOM_SCRIPT steps — a terminal-styled
 * reading pane (no kaneai source to port; design per user 2026-08-26:
 * "beautiful terminal"). Chrome bar with traffic lights + interpreter +
 * exec id; line-numbered mono body; the viewer's view-only footer voice.
 * Follows the ApiModal lessons: customHeaderComponent (no h4/WCAG),
 * maxHeight cap with internal scroll, --ev-font restated (portal).
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var TERM_THEME_KEY = 'ev-script-term-theme';
var storedTheme = function storedTheme() {
  try {
    return localStorage.getItem(TERM_THEME_KEY) === 'light' ? 'light' : 'dark';
  } catch (_unused) {
    return 'dark';
  }
};
export function CustomScriptModal(_ref) {
  var _script$execId;
  var script = _ref.script,
    onClose = _ref.onClose;
  // Terminal-local theme (user 2026-08-26): dark default, persisted flip
  // to a GitHub-light palette — same localStorage idiom as the dock.
  var _useState = useState(storedTheme),
    _useState2 = _slicedToArray(_useState, 2),
    theme = _useState2[0],
    setTheme = _useState2[1];
  var toggleTheme = function toggleTheme() {
    var next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem(TERM_THEME_KEY, next);
    } catch (_unused2) {
      // Preference just won't persist.
    }
  };
  var lines = script.scriptContent.replace(/\n$/, '').split('\n');
  var title = script.command ? "".concat(script.command).concat(script.execId ? " \xB7 ".concat(script.execId) : '') : (_script$execId = script.execId) !== null && _script$execId !== void 0 ? _script$execId : 'script';
  return /*#__PURE__*/_jsx(LTModal, {
    isOpen: true,
    ariaLabel: "Custom script",
    onClose: onClose,
    customHeaderComponent: /*#__PURE__*/_jsx("div", {
      style: {
        flex: 1,
        textAlign: 'left'
      },
      children: /*#__PURE__*/_jsx("span", {
        style: {
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '20px',
          letterSpacing: '-0.15px'
        },
        children: "Custom script"
      })
    }),
    extraStyle: {
      width: '760px',
      maxWidth: '92vw',
      maxHeight: '86vh',
      overflow: 'hidden',
      fontFamily: 'var(--ev-font)'
    },
    children: /*#__PURE__*/_jsx("div", {
      className: styles.termModalBody,
      children: /*#__PURE__*/_jsxs("div", {
        className: "".concat(styles.termWindow, " ").concat(theme === 'light' ? styles.termWindowLight : ''),
        children: [/*#__PURE__*/_jsxs("div", {
          className: styles.termBar,
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.termGlyph,
            "aria-hidden": "true",
            children: /*#__PURE__*/_jsxs("svg", {
              width: "14",
              height: "14",
              viewBox: "0 0 16 16",
              fill: "none",
              children: [/*#__PURE__*/_jsx("path", {
                d: "M2.5 4l3.2 3-3.2 3",
                stroke: "#3fb950",
                strokeWidth: "1.6",
                strokeLinecap: "round",
                strokeLinejoin: "round"
              }), /*#__PURE__*/_jsx("path", {
                d: "M8 10.5h5",
                stroke: "#8b949e",
                strokeWidth: "1.6",
                strokeLinecap: "round"
              })]
            })
          }), /*#__PURE__*/_jsx("span", {
            className: styles.termTitle,
            title: title,
            children: title
          }), script.timeoutSec !== null && /*#__PURE__*/_jsxs("span", {
            className: styles.termTimeout,
            children: ["Timeout: ", script.timeoutSec, "s"]
          }), /*#__PURE__*/_jsx("button", {
            type: "button",
            className: styles.termThemeBtn,
            "aria-label": theme === 'dark' ? 'Switch terminal to light theme' : 'Switch terminal to dark theme',
            onClick: toggleTheme,
            children: theme === 'dark' ? /*#__PURE__*/_jsxs("svg", {
              width: "14",
              height: "14",
              viewBox: "0 0 16 16",
              fill: "none",
              "aria-hidden": "true",
              children: [/*#__PURE__*/_jsx("circle", {
                cx: "8",
                cy: "8",
                r: "3.2",
                stroke: "currentColor",
                strokeWidth: "1.3"
              }), /*#__PURE__*/_jsx("path", {
                d: "M8 1.5v1.8M8 12.7v1.8M1.5 8h1.8M12.7 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3",
                stroke: "currentColor",
                strokeWidth: "1.3",
                strokeLinecap: "round"
              })]
            }) : /*#__PURE__*/_jsx("svg", {
              width: "14",
              height: "14",
              viewBox: "0 0 16 16",
              fill: "none",
              "aria-hidden": "true",
              children: /*#__PURE__*/_jsx("path", {
                d: "M13.5 9.5A6 6 0 1 1 6.5 2.5a4.8 4.8 0 0 0 7 7z",
                stroke: "currentColor",
                strokeWidth: "1.3",
                strokeLinejoin: "round"
              })
            })
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: styles.termScroll,
          children: /*#__PURE__*/_jsx("table", {
            className: styles.termCode,
            children: /*#__PURE__*/_jsx("tbody", {
              children: lines.map(function (line, i) {
                return (
                  /*#__PURE__*/
                  // eslint-disable-next-line react/no-array-index-key
                  _jsxs("tr", {
                    children: [/*#__PURE__*/_jsx("td", {
                      className: styles.termLineNo,
                      children: i + 1
                    }), /*#__PURE__*/_jsx("td", {
                      className: styles.termLine,
                      children: line === '' ? ' ' : line
                    })]
                  }, i)
                );
              })
            })
          })
        })]
      })
    })
  });
}